import React, { useState, useEffect, useRef } from 'react';
import { ScrollArea } from '@radix-ui/react-scroll-area';
import ReactMarkdown from 'react-markdown';
import axios from 'axios';
import remarkGfm from 'remark-gfm';  // GitHub Flavored Markdown (for tables)
import rehypeRaw from 'rehype-raw';  // Allows raw HTML for rendering images
import 'github-markdown-css';  // Import GitHub-style markdown CSS

const OPENAI_API_KEY = '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'; // Replace with your actual API key

const ChatApp = () => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [typing, setTyping] = useState(false);
  const [chatHistory, setChatHistory] = useState([
    { id: 1, title: 'Chat Session 1', messages: [{ sender: 'bot', text: 'Hello, how can I assist you today?' }] },
    { id: 2, title: 'Chat Session 2', messages: [{ sender: 'bot', text: 'What can I help you with?' }] },
  ]);
  const [currentChatId, setCurrentChatId] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Function to send messages to OpenAI API
  const handleSend = async () => {
    if (input.trim() === '') return;

    const userMessage = { sender: 'user', text: input };
    const updatedMessages = [...messages, userMessage];

    setMessages(updatedMessages);
    setInput('');
    setTyping(true);

    try {
      const response = await axios.post(
        'https://api.openai.com/v1/chat/completions',
        {
          model: 'gpt-4',
          messages: updatedMessages.map((msg) => ({
            role: msg.sender === 'user' ? 'user' : 'assistant',
            content: msg.text,
          })),
        },
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${OPENAI_API_KEY}`,
          },
        }
      );

      const botResponse = {
        sender: 'bot',
        text: response.data.choices[0].message.content,
      };

      setMessages((prev) => [...prev, botResponse]);
    } catch (error) {
      console.error('Error fetching response from ChatGPT', error);
      const errorMessage = { sender: 'bot', text: 'Sorry, there was an error.' };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setTyping(false);
    }
  };

  const loadChat = (chat) => {
    setMessages(chat.messages);
    setCurrentChatId(chat.id);
  };

  const clearHistory = () => {
    setChatHistory([]);
    setMessages([]);
    setCurrentChatId(null);
  };

  // Automatically renders markdown and applies styles from `github-markdown-css`
  const renderMessage = (msg) => (
    <div className="markdown-body">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]} // Enables GitHub-flavored markdown features (tables, strikethrough, etc.)
        rehypePlugins={[rehypeRaw]}  // Allows raw HTML in markdown
      >
        {msg.text}
      </ReactMarkdown>
    </div>
  );

  return (
    <div className="flex h-screen bg-gray-900 text-white">
      {/* Sidebar */}
      <div className={`transition-all duration-300 ${sidebarOpen ? 'w-64' : 'w-16'} bg-gray-800 flex flex-col`}>
        <div className="p-4 flex justify-between items-center border-b border-gray-700">
          {sidebarOpen ? (
            <>
              <h2 className="text-xl font-bold">Chat History</h2>
              <button className="text-gray-400 hover:text-white" onClick={() => setSidebarOpen(false)}>
                ✕
              </button>
            </>
          ) : (
            <button className="w-full text-center text-2xl" onClick={() => setSidebarOpen(true)}>
              ☰
            </button>
          )}
        </div>
        {sidebarOpen && (
          <>
            <ScrollArea className="flex-1 p-2 overflow-auto">
              {chatHistory.length > 0 ? (
                chatHistory.map((chat) => (
                  <div
                    key={chat.id}
                    className={`p-3 mb-2 rounded cursor-pointer hover:bg-gray-700 ${
                      currentChatId === chat.id ? 'bg-gray-700' : ''
                    }`}
                    onClick={() => loadChat(chat)}
                  >
                    {chat.title}
                  </div>
                ))
              ) : (
                <p className="p-3 text-gray-400">No chat history.</p>
              )}
            </ScrollArea>
            <button className="p-4 bg-red-600 hover:bg-red-700 text-white text-sm" onClick={clearHistory}>
              Clear History
            </button>
          </>
        )}
      </div>

      {/* Main Chat Area */}
      <div className="flex-1 flex flex-col">
        <div className="flex-1 p-6 overflow-y-auto">
          {messages.length > 0 ? (
            messages.map((msg, index) => (
              <div
                key={index}
                className={`mb-4 flex ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-xl px-4 py-2 rounded-lg ${
                    msg.sender === 'user' ? 'bg-blue-600 text-white' : 'bg-gray-700 text-gray-300'
                  }`}
                >
                  {renderMessage(msg)}
                </div>
              </div>
            ))
          ) : (
            <p className="text-center text-gray-500">Start the conversation by typing a message below.</p>
          )}
          {typing && (
            <div className="mb-4 flex justify-start">
              <div className="max-w-xs px-4 py-2 rounded-lg bg-gray-700 text-gray-300 italic">Typing...</div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input Field */}
        <div className="p-4 bg-gray-800 border-t border-gray-700 flex">
          <input
            className="flex-1 p-3 bg-gray-700 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSend()}
            placeholder="Type your message..."
          />
          <button
            className="ml-4 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg disabled:bg-blue-500"
            onClick={handleSend}
            disabled={input.trim() === ''}
          >
            Send
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChatApp;
